import React from "react";
import { Truck, PackageCheck, Navigation, GripVertical, Paperclip } from "lucide-react";
import {
  sLabel, sColor, mapsUrl,
  getPrimaryStopDetails, getPrimaryStopSummary,
  downloadPdf, getReadyBadgeLabel, getDdtBadgeLabel,
} from "../helpers";
import { UrgencyBadge } from "../UrgencyBadge";
import { BarcodeScanner } from "../BarcodeScanner";
import { DraggableList } from "../DraggableList";
import { DriverHeader } from "../DriverHeader";

export function ListScreen(ctx) {
  const {
    driverInfo, active, completed, sortedActive, listTab, setListTab,
    myLocation, showScanner, setShowScanner,
    pushEnabled, installPrompt, setInstallPrompt,
    handleScan, navigateTo, togglePush, logout, goBack,
    etaBadge, handleReorder,
  } = ctx;

  const shown = listTab === "active" ? sortedActive : completed;

  const renderItem = (item, idx, onPointer) => (
    <div className="flex items-stretch">
      {onPointer && sortedActive.length > 1 && (
        <div
          className="flex items-center pl-2 pr-1 cursor-grab touch-none select-none bg-zinc-50 border-r border-zinc-100"
          onTouchStart={onPointer}
          onMouseDown={onPointer}
          data-testid={`drag-handle-${item.tracking_number}`}
        >
          <GripVertical size={18} className="text-zinc-300" />
        </div>
      )}
      <div className="flex-1 bg-white border border-zinc-200 p-3 flex items-center justify-between gap-3" data-testid={`work-card-${item.tracking_number}`}>
        <button onClick={() => { navigateTo("detail", item); }} className="flex-1 flex items-center gap-2.5 text-left min-w-0">
          <div className={`w-1.5 h-12 rounded-full ${sColor(item.status)}`} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              {item._type === "shipment" ? <Truck size={13} className="text-[#FF5200]" /> : <PackageCheck size={13} className="text-purple-500" />}
              <span className="text-[10px] uppercase tracking-wide text-zinc-400">{item._type === "shipment" ? "Spedizione" : "Ritiro"}</span>
            </div>
            <p className="text-sm font-bold text-[#09090B] leading-tight uppercase truncate">{getPrimaryStopSummary(item).title}</p>
            {getPrimaryStopSummary(item).address && <p className="text-[11px] text-zinc-700 leading-tight">{getPrimaryStopSummary(item).address}</p>}
            <p className="text-[11px] text-zinc-500 leading-tight">{getPrimaryStopSummary(item).locality}</p>
            <div className="flex flex-wrap items-center gap-1 mt-0.5">
              {myLocation && etaBadge(item) && <span className="text-[10px] font-bold text-[#FF5200]" data-testid={`job-distance-${item.tracking_number}`}>{etaBadge(item)}</span>}
              <p className="text-[10px] text-zinc-400 truncate">{item.tracking_number}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <UrgencyBadge job={item} context="list" />
              {getReadyBadgeLabel(item) && <span className="px-2 py-1 bg-green-50 text-green-700 text-[11px] font-bold rounded-sm" data-testid={`driver-ready-badge-${item.tracking_number}`}>{getReadyBadgeLabel(item)}</span>}
              {getDdtBadgeLabel(item) && <button type="button" onClick={() => downloadPdf(item.ddt_files[0])} className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 text-[11px] font-bold rounded-sm hover:bg-zinc-200" data-testid={`driver-ddt-badge-${item.tracking_number}`}><Paperclip size={12} /> {getDdtBadgeLabel(item)}</button>}
            </div>
          </div>
        </button>
        <div className="flex flex-col items-end gap-2">
          {getPrimaryStopDetails(item) && (
            <a href={mapsUrl(getPrimaryStopDetails(item), myLocation ? [myLocation.lat, myLocation.lng] : null)} target="_blank" rel="noopener noreferrer" className="h-9 w-9 flex items-center justify-center border border-zinc-200 rounded-sm text-[#09090B] bg-white" data-testid={`navigate-job-${item.tracking_number}`}>
              <Navigation size={14} />
            </a>
          )}
          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase text-white rounded-full ${sColor(item.status)}`}>{sLabel(item.status)}</span>
          <span className="text-[10px] text-zinc-400">{(item.colli || []).reduce((s, c) => s + (parseInt(c.quantita) || 1), 0) || 0} colli</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="driver-app">
      {showScanner && <BarcodeScanner onScan={handleScan} onClose={() => setShowScanner(false)} />}
      <DriverHeader showBack title="Modifica Giro" driverInfo={driverInfo} goBack={goBack} navigateTo={navigateTo} togglePush={togglePush} pushEnabled={pushEnabled} setShowScanner={setShowScanner} installPrompt={installPrompt} setInstallPrompt={setInstallPrompt} logout={logout} />
      <div className="flex border-b border-zinc-200 mx-4 mt-2 mb-2">
        {[["active", `Attivi oggi (${active.length})`], ["completed", `Completati oggi (${completed.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setListTab(k)} className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 ${listTab === k ? (k === "active" ? "border-[#FF5200] text-[#FF5200]" : "border-green-500 text-green-600") : "border-transparent text-zinc-400"}`} data-testid={`driver-tab-${k}`}>{l}</button>
        ))}
      </div>
      {listTab === "active" && sortedActive.length > 1 && (
        <p className="text-[10px] text-zinc-400 text-center mb-2 flex items-center justify-center gap-1"><GripVertical size={12} /> Tieni premuto per riordinare · Ordine distinta come default</p>
      )}
      <div className="px-4 pb-8">
        {shown.length === 0 ? <p className="text-center text-zinc-400 text-sm py-12">{listTab === "active" ? "Nessun lavoro attivo" : "Nessun completato"}</p>
        : listTab === "active" && sortedActive.length > 1 ? <DraggableList items={sortedActive} onReorder={handleReorder} renderItem={renderItem} />
        : <div className="space-y-2">{shown.map((item, i) => <div key={item.id}>{renderItem(item, i, null)}</div>)}</div>}
      </div>
    </div>
  );
}
